<script>
import Auth from "../../layouts/auth";
import appConfig from "../../../../app.config";

import { required, email } from "vuelidate/lib/validators";

/**
 * Login component
 */
export default {
  page: {
    title: "Inicio de sesión",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      email: null,
      password: null,
      submitted: false,
      authError: null,
      tryingToLogIn: false,
      isAuthError: false,
      showPass: false,
      loading: false,
      message: "",
    };
  },
  components: {
    Auth,
  },
  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
    forgotUrl() {
      return `https://sso.emision.co/password/email?callback=${encodeURIComponent(window.location.origin + "/login")}`;
    },
    // Credenciales vs. servidor, red o límite de intentos: no culpar al usuario por un fallo nuestro
    loginErrorText() {
      const m = this.notification && this.notification.message;
      // Solo los errores de AuthAPI traen `kind`; un texto de otra pantalla se muestra tal cual
      if (typeof m === "string") return m;
      const kind = m && m.kind;
      if (kind === "network" || kind === "server") {
        return "No pudimos conectar con el servidor. Inténtalo de nuevo en unos minutos.";
      }
      if (kind === "throttled") return "Demasiados intentos. Espera unos minutos antes de volver a intentarlo.";
      return "Correo electrónico o contraseña no válidos.";
    },
    notificationAutoCloseDuration() {
      return this.$store && this.$store.state.notification ? 5 : 0;
    },
  },
  watch: {
    // Un error de login (store/notification) o un mensaje nuevo detiene la carga
    "notification.message"(msg) {
      if (msg) this.loading = false;
    },
  },
  beforeDestroy() {
    clearTimeout(this.loadingTimer);
  },
  created() {
    if (this.loggedIn) {
      this.$router.push("/");
    }
  },
  validations: {
    email: {
      required,
      email,
    },
    password: {
      required,
    },
  },
  methods: {
    // Try to log the user in with the username
    // and password they provided.
    tryToLogIn() {
      if (this.loading) return
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$nextTick(() => {
          const el = document.getElementById(this.$v.email.$error ? "emailaddress" : "password");
          if (el) el.focus({ preventScroll: true });
        });
        return;
      } else {

        const { email, password } = this;
        if (email && password) {
          // Estado de carga: se apaga al llegar un error (watcher de `notification`)
          // o, como red de seguridad, a los 15 s. En éxito la página recarga.
          this.loading = true
          clearTimeout(this.loadingTimer)
          this.loadingTimer = setTimeout(() => { this.loading = false }, 15000)
          this.$store.dispatch("auth/login", {
            email,
            password,
          });
          this.$store.dispatch("notification/clear");
        } else {
          this.loading = false
        }
      }
    },
    showPassword() {
      this.showPass = !this.showPass;
    },
  },
};
</script>

<template>
  <Auth>
    <header class="auth-head">
      <h1 class="auth-title">
        Ingresa a tu panel de <span class="auth-nowrap">SG-SST</span>
      </h1>
      <p class="auth-lead">
        Estándares mínimos de la Resolución 0312 de 2019 en un solo lugar.
      </p>
    </header>

    <form
      class="auth-form"
      action="#"
      novalidate
      :aria-busy="loading ? 'true' : 'false'"
      @submit.prevent="tryToLogIn"
    >
      <div class="auth-field">
        <label for="emailaddress">Correo electrónico</label>
        <input
          id="emailaddress"
          v-model="email"
          class="auth-input"
          type="email"
          name="email"
          autocomplete="username"
          spellcheck="false"
          placeholder="nombre@suempresa.co"
          :class="{ 'is-invalid': submitted && $v.email.$error }"
          :aria-invalid="submitted && $v.email.$error ? 'true' : 'false'"
          :aria-describedby="submitted && $v.email.$error ? 'email-error' : null"
        />
        <div
          v-if="submitted && $v.email.$error"
          id="email-error"
          class="auth-error"
        >
          <span v-if="!$v.email.required">Correo electrónico requerido.</span>
          <span v-if="!$v.email.email">Ingresa un correo electrónico válido.</span>
        </div>
      </div>

      <div class="auth-field">
        <div class="auth-field__row">
          <label for="password">Contraseña</label>
          <a
            class="auth-link"
            :href="forgotUrl"
            >¿Olvidaste tu contraseña?</a
          >
        </div>
        <div class="auth-input-wrap">
          <input
            id="password"
            v-model="password"
            class="auth-input"
            name="password"
            autocomplete="current-password"
            :type="showPass ? 'text' : 'password'"
            :class="{ 'is-invalid': submitted && $v.password.$error }"
            :aria-invalid="submitted && $v.password.$error ? 'true' : 'false'"
            :aria-describedby="submitted && !$v.password.required ? 'password-error' : null"
          />
          <button
            type="button"
            class="auth-toggle"
            aria-label="Mostrar contraseña"
            :aria-pressed="showPass ? 'true' : 'false'"
            @click="showPassword"
          >
            <svg
              v-if="!showPass"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z" />
              <circle cx="12" cy="12" r="3" />
              <path d="M4 4l16 16" />
            </svg>
          </button>
        </div>
        <div
          v-if="submitted && !$v.password.required"
          id="password-error"
          class="auth-error"
        >
          La contraseña es requerida.
        </div>
      </div>

      <button class="auth-submit" type="submit" :disabled="loading">
        <b-spinner v-if="loading" small class="auth-submit__spinner" aria-hidden="true"></b-spinner>
        <span>{{ loading ? "Ingresando…" : "Ingresar al panel" }}</span>
      </button>

      <!-- Región reservada para errores y avisos de sesión (role="alert") -->
      <div class="auth-alert" role="alert">
        <b-alert
          :variant="notification.type"
          v-if="notification.message"
          :show="notificationAutoCloseDuration"
          dismissible
          dismiss-label="Cerrar aviso"
          >{{ loginErrorText }}</b-alert
        >
        <b-alert
          variant="danger"
          v-model="isAuthError"
          :show="notificationAutoCloseDuration"
          dismissible
          dismiss-label="Cerrar aviso"
          >{{ authError }}</b-alert
        >
      </div>
    </form>
  </Auth>
</template>
